import { Coins } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import LoadingSpinner from "../ui/LoadingSpinner";
import { format, startOfMonth } from "date-fns";

const NimiCreditsCard = () => {
  const { data: creditStats, isLoading } = useQuery({
    queryKey: ['credit-stats'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      // Get current balance
      const { data: membership } = await supabase
        .from('user_memberships')
        .select('credits_balance')
        .eq('user_id', user.id)
        .maybeSingle();

      // Get total credits spent from tickets
      const { data: tickets } = await supabase
        .from('tickets')
        .select('events(credit_cost), quantity')
        .eq('user_id', user.id)
        .eq('payment_method', 'credits');

      const totalSpent = tickets?.reduce((acc, ticket) => {
        return acc + (ticket.events?.credit_cost || 0) * ticket.quantity;
      }, 0) || 0;

      // Get credits received this month from credit pack purchases
      const monthStart = startOfMonth(new Date()).toISOString();
      const { data: creditPurchases } = await supabase
        .from('credit_purchases')
        .select('credits_amount')
        .eq('user_id', user.id)
        .gte('created_at', monthStart);

      const creditPackCredits = creditPurchases?.reduce((acc, p) => acc + p.credits_amount, 0) || 0;

      // Get subscription credits granted this month
      const { data: campaignPurchases } = await supabase
        .from('campaign_purchases')
        .select('credits_granted')
        .eq('user_id', user.id)
        .gte('created_at', monthStart);

      const subscriptionCredits = campaignPurchases?.reduce((acc, p) => acc + p.credits_granted, 0) || 0;

      // Get referral credits (from partner code redemptions this month)
      const { data: redemptions } = await supabase
        .from('partner_code_redemptions')
        .select('credits_granted')
        .eq('user_id', user.id)
        .gte('redeemed_at', monthStart);

      const referralCredits = redemptions?.reduce((acc, r) => acc + r.credits_granted, 0) || 0;

      const totalReceived = creditPackCredits + subscriptionCredits + referralCredits;

      return {
        balance: membership?.credits_balance || 0,
        spent: totalSpent,
        received: totalReceived,
      };
    },
  });

  if (isLoading) return <LoadingSpinner />;

  const currentMonth = format(new Date(), 'MMMM');

  return (
    <div className="bg-card rounded-lg p-4 shadow-md">
      <div className="flex items-center mb-4">
        <Coins className="text-secondary mr-2" />
        <h2 className="text-lg font-semibold text-foreground">Nimi Credits</h2>
        <span className="text-sm text-muted-foreground ml-2 italic">{currentMonth} snapshot</span>
      </div>
      
      <div className="space-y-4">
        <div>
          <p className="text-sm text-muted-foreground">Current Balance</p>
          <p className="text-xl font-semibold text-primary">{creditStats?.balance}</p>
        </div>
        
        <div>
          <p className="text-sm text-muted-foreground">Credits Spent</p>
          <p className="text-xl font-semibold text-primary">{creditStats?.spent}</p>
        </div>
        
        <div>
          <p className="text-sm text-muted-foreground">Credits Received</p>
          <p className="text-xl font-semibold text-primary">{creditStats?.received}</p>
        </div>
      </div>
    </div>
  );
};

export default NimiCreditsCard;
